import { Suspense, useMemo, useRef, useState } from 'react';
import { Camera, Palette } from 'lucide-react';
import type { ResolvedDecorValues } from '@peles/shared';
import { EntityModal } from '../common';
import { SegmentedControl } from '../common/SegmentedControl';
import { FloorPlan3DView } from './FloorPlan3DView';
import type { FloorPlanRealisticHandle, RealisticCameraMode } from './FloorPlanRealisticView';
import { LazyRealisticView, hasWebGL, loadRealisticView } from './realisticViewLoader';
import type { LightingMood } from './realisticChoices';
import { ToolbarToggleGroup } from './ToolbarToggleGroup';
import type { BuildSceneOptions } from './three-d';
import type { CanvasItem } from './types';

// The realistic view is loaded on first use (see realisticViewLoader).
const FloorPlanRealisticView = LazyRealisticView;
export { hasWebGL, loadRealisticView };

type ViewKind = 'realistic' | 'diagram';

interface FloorPlan3DModalProps {
  open: boolean;
  onClose: () => void;
  items: CanvasItem[];
  room?: BuildSceneOptions['room'];
  fallbackWidthFt?: number;
  fallbackHeightFt?: number;
  chairSides?: BuildSceneOptions['chairSides'];
  /** Plan name, shown in the modal title. */
  planName?: string;
  /**
   * The event's look, with this plan's changes (`resolveDecor`): linen,
   * chairs, flowers, tableware, walls and floor. Left out, the default look.
   */
  decor?: Partial<ResolvedDecorValues>;
  /** Where the look is set — offered as a button when given (the office only). */
  onEditLook?: () => void;
  /** Opened over another dialog (the look's preview): drawn above it. */
  stacked?: boolean;
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId: string;
}

function Toggle({ label, checked, onChange, testId }: ToggleProps) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300"
        data-testid={testId}
      />
      {label}
    </label>
  );
}

/**
 * The 3D views of a floor plan: the realistic walk-through (WebGL, lit and
 * dressed — linens, chairs, place settings, centrepieces) and the diagram
 * (the SVG renderer, which runs anywhere and prints). Same objects, same feet, same chairs as
 * the 2D canvas — only the camera changes, so what a client is shown here is
 * the plan that was drawn rather than a second drawing kept in step by hand.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  );
}

export function FloorPlan3DModal({
  open,
  onClose,
  items,
  room,
  fallbackWidthFt,
  fallbackHeightFt,
  chairSides,
  planName,
  decor,
  onEditLook,
  stacked = false,
}: FloorPlan3DModalProps) {
  const [showChairs, setShowChairs] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showWalls, setShowWalls] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [perspective, setPerspective] = useState(true);
  // Asked once the modal opens, not for every floor plan that merely mounts it.
  const webgl = useMemo(() => open && hasWebGL(), [open]);
  const [chosenView, setView] = useState<ViewKind | null>(null);
  const view: ViewKind = chosenView ?? (webgl ? 'realistic' : 'diagram');
  // The tour flies the camera and drops the furniture in: with reduced motion
  // asked for, open on Look around and leave the tour as a choice. Asked each
  // time it opens — the modal stays mounted while closed — and a camera the
  // person chose themselves stands.
  const reducedMotion = useMemo(() => open && prefersReducedMotion(), [open]);
  const [chosenCameraMode, setCameraMode] = useState<RealisticCameraMode | null>(null);
  const cameraMode: RealisticCameraMode = chosenCameraMode ?? (reducedMotion ? 'orbit' : 'tour');
  const [lighting, setLighting] = useState<LightingMood>('evening');
  const [showCenterpieces, setShowCenterpieces] = useState(true);
  const [showPlaceSettings, setShowPlaceSettings] = useState(true);
  const [showStringLights, setShowStringLights] = useState(true);
  const realisticRef = useRef<FloorPlanRealisticHandle>(null);

  const savePicture = () => {
    const url = realisticRef.current?.capture();
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(planName || 'floor-plan').replace(/[^\w-]+/g, '-')}-3d.png`;
    link.click();
  };

  const realistic = view === 'realistic';

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={planName ? `3D View — ${planName}` : '3D View'}
      size="xl"
      overlayClassName={stacked ? 'z-[60]' : undefined}
    >
      <div className="space-y-3">
        {webgl && (
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl<ViewKind>
              ariaLabel="How to show the plan"
              value={view}
              onChange={setView}
              options={[
                { id: 'realistic', label: 'Realistic' },
                { id: 'diagram', label: 'Diagram' },
              ]}
            />
            {realistic && (
              <SegmentedControl<RealisticCameraMode>
                ariaLabel="Camera"
                value={cameraMode}
                onChange={setCameraMode}
                options={[
                  { id: 'tour', label: 'Tour' },
                  { id: 'orbit', label: 'Look around' },
                  { id: 'walk', label: 'Walk through' },
                ]}
              />
            )}
            {realistic && (
              <button
                type="button"
                onClick={savePicture}
                className="ml-auto inline-flex items-center gap-1.5 min-h-11 sm:min-h-0 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                data-testid="save-3d-picture"
              >
                <Camera className="h-4 w-4" />
                Save picture
              </button>
            )}
          </div>
        )}

        {realistic ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600">
            <ToolbarToggleGroup<LightingMood>
              ariaLabel="Lighting"
              value={lighting}
              onChange={setLighting}
              data-testid="lighting-3d"
              options={[
                { value: 'day', label: 'Daylight' },
                { value: 'evening', label: 'Evening' },
                { value: 'candlelight', label: 'Candlelight' },
              ]}
            />
            {onEditLook && (
              <button
                type="button"
                onClick={onEditLook}
                className="inline-flex items-center gap-1.5 min-h-11 sm:min-h-0 px-2.5 py-1 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50"
                data-testid="edit-look-3d"
              >
                <Palette className="h-3.5 w-3.5" />
                Linen, chairs & flowers…
              </button>
            )}
            <Toggle
              label="Chairs"
              checked={showChairs}
              onChange={setShowChairs}
              testId="toggle-chairs-3d"
            />
            <Toggle
              label="Centrepieces"
              checked={showCenterpieces}
              onChange={setShowCenterpieces}
              testId="toggle-centerpieces-3d"
            />
            <Toggle
              label="String lights"
              checked={showStringLights}
              onChange={setShowStringLights}
              testId="toggle-string-lights-3d"
            />
            <Toggle
              label="Place settings"
              checked={showPlaceSettings}
              onChange={setShowPlaceSettings}
              testId="toggle-place-settings-3d"
            />
            <Toggle
              label="Table numbers"
              checked={showLabels}
              onChange={setShowLabels}
              testId="toggle-labels-3d"
            />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Toggle
              label="Chairs"
              checked={showChairs}
              onChange={setShowChairs}
              testId="toggle-chairs-3d"
            />
            <Toggle
              label="Table numbers"
              checked={showLabels}
              onChange={setShowLabels}
              testId="toggle-labels-3d"
            />
            <Toggle
              label="Walls"
              checked={showWalls}
              onChange={setShowWalls}
              testId="toggle-walls-3d"
            />
            <Toggle
              label="Grid"
              checked={showGrid}
              onChange={setShowGrid}
              testId="toggle-grid-3d"
            />
            <Toggle
              label="Perspective"
              checked={perspective}
              onChange={setPerspective}
              testId="toggle-perspective-3d"
            />
            <span className="ml-auto text-xs text-gray-400">Drag to orbit · scroll to zoom</span>
          </div>
        )}

        {realistic ? (
          <>
            <Suspense
              fallback={
                <div
                  className="aspect-[25/16] w-full rounded-lg bg-gray-100 animate-pulse"
                  data-testid="realistic-3d-loading"
                />
              }
            >
              <FloorPlanRealisticView
                ref={realisticRef}
                className="aspect-[25/16] w-full"
                mode={cameraMode}
                items={items}
                room={room}
                fallbackWidthFt={fallbackWidthFt}
                fallbackHeightFt={fallbackHeightFt}
                chairSides={chairSides}
                showChairs={showChairs}
                showLabels={showLabels}
                lighting={lighting}
                decor={decor}
                showCenterpieces={showCenterpieces}
                showPlaceSettings={showPlaceSettings}
                showStringLights={showStringLights}
                subtitle={planName}
                onTourEnd={() => setCameraMode('orbit')}
                onUnsupported={() => setView('diagram')}
              />
            </Suspense>
            <p className="text-xs text-gray-400">
              {cameraMode === 'tour'
                ? 'A guided tour of the room — pick Look around or Walk through to take the camera'
                : cameraMode === 'walk'
                  ? 'Drag to look around · W A S D or the arrow keys to walk · Q / E to turn'
                  : 'Drag to turn · scroll or pinch to zoom · right-drag to slide'}
            </p>
          </>
        ) : (
          <div className="aspect-[25/16] w-full">
            <FloorPlan3DView
              items={items}
              room={room}
              fallbackWidthFt={fallbackWidthFt}
              fallbackHeightFt={fallbackHeightFt}
              chairSides={chairSides}
              showChairs={showChairs}
              showLabels={showLabels}
              showWalls={showWalls}
              showGrid={showGrid}
              perspective={perspective}
            />
          </div>
        )}
      </div>
    </EntityModal>
  );
}
